import React from 'react';
import * as clipboard from 'clipboard-polyfill';
import { useTranslate } from 'hooks/useTranslate';
import { useTheme } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@mui/system';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import ArrowFatUpIcon from 'assets/icons/ph_arrow-fat-up.svg?react';
import ArrowFatDownIcon from 'assets/icons/ph_arrow-fat-down.svg?react';
import ChatIcon from 'assets/icons/ph_chat.svg?react';
import GoldMedalIcon from 'assets/icons/marketeq_gold-medal-6.svg?react';
import CopyIcon from 'assets/icons/fluent_copy.svg?react';
import PencilDotsIcon from 'assets/icons/pepicons-pencil_dots-x.svg?react';
import FavoritesIcon from 'assets/icons/fluent_favorites.svg?react';
import SkullIcon from 'assets/icons/skull.svg?react';
import StarCheckedIcon from 'assets/icons/star_checked.svg?react';
import LinearProgress from 'components/LinearProgress';
import { changeVote } from 'actions/questions_advices';
import { toggleFavorite } from 'actions/profession';
import { addMessage } from 'actions/error';
import Message from 'components/Snackbars/Message';

const QuestionActions = styled('div')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  maxWidth: '100%',
  alignItems: 'center',
  marginTop: 5,
  position: 'relative',
  left: -10,
  [theme.breakpoints.down('md')]: {
    position: 'relative',
    left: -10
  }
}));

// Toolbar-action style. Salad-green (theme.palette.primary.main, #79c912)
// label + icon, no border, no fill. Sits inside an action row beside
// other equal-weight actions (Vote, Answer, Share, Save, Report). On the
// `advices` block these actions render on a dark background, so we keep
// the existing white fallback for that surface.
const TOOLBAR_FG = (theme, type) =>
  type === 'advices' ? theme.palette.white.main : theme.palette.primary.main;

const IconButtonStyled = styled(IconButton)(({ hide, type, theme }) => ({
  display: hide === 'true' ? 'none' : 'flex',
  color: TOOLBAR_FG(theme, type),
  '& svg': {
    width: 20,
    height: 20
  },
  '& svg path': {
    fill: TOOLBAR_FG(theme, type)
  }
}));

const ButtonStyled = styled(Button)(({ hide, type, stroke, isFavorite, theme }) => ({
  fontSize: 14,
  padding: '5px 10px',
  color: TOOLBAR_FG(theme, type),
  display: hide === 'true' ? 'none' : 'flex',
  alignItems: 'flex-start',
  '& svg': {
    width: 20,
    height: 20
  },
  '& svg path': {
    fill: TOOLBAR_FG(theme, type),
    stroke: type === 'advices' ? stroke || 'initial' : 'initial'
  },
  '&:hover': {
    backgroundColor: 'rgba(121, 201, 18, 0.08)',
    color: TOOLBAR_FG(theme, type),
    opacity: 0.85
  },
  // Active "favourited" state: keep the brighter lime so the toggled
  // state still reads as "on" even though everything else around it is
  // already salad green.
  ...(isFavorite === 'true'
    ? {
        '& svg path': {
          fill: 'rgb(185, 248, 51)',
          stroke: 'rgb(185, 248, 51)'
        }
      }
    : {})
}));

const VotesWrapper = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center'
}));

const Votes = styled(Typography)(({ type, theme }) => ({
  fontSize: 14,
  minWidth: 5,
  padding: 5,
  color: TOOLBAR_FG(theme, type)
}));

const SuccessMessage = styled(Typography)(({ theme, type }) => ({
  color: type === 'advices' ? theme.palette.white.main : theme.palette.primary.main,
  fontSize: 14,
  lineHeight: '22px',
  fontWeight: 500
}));

const TEMP_HIDDEN_BUTTONS = [2, 5];

const ActionBlock = ({
  _id,
  votesTotal,
  isAnswer,
  children,
  handleCheckAuth,
  handleGiveAnswer,
  isFavorite: isFavoriteProp,
  entity,
  readOnly,
  theme: themeProp,
  actions,
  answers,
  type
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [loading, setLoading] = React.useState(false);
  const [fullActions, setFullActions] = React.useState(false);
  const [showMore, setShowMore] = React.useState(false);
  const [hiddenActions, setHiddenActions] = React.useState([false, false, false, false, false]);
  const [isCopied, setIsCopied] = React.useState(false);
  const [isFavorite, setIsFavorite] = React.useState(isFavoriteProp);
  const t = useTranslate('Questions');
  const actionsRef = React.useRef(null);
  const isMobile = useSelector((state) => state.app.isMobile);

  const handleAddVote = React.useCallback(async () => {
    if (handleCheckAuth()) {
      const result = await dispatch(
        changeVote(_id, { vote: 'positive' }, isAnswer ? `${entity}/answers` : entity)
      );
      if (!(result instanceof Error)) {
        dispatch(addMessage(new Message(t('VotingSuccess'), 'success')));
      }
      await actions.load();
    }
  }, [handleCheckAuth, dispatch, t, entity, isAnswer, _id, actions]);

  const handleRemoveVote = React.useCallback(async () => {
    if (handleCheckAuth()) {
      const result = await dispatch(
        changeVote(_id, { vote: 'negative' }, isAnswer ? `${entity}/answers` : entity)
      );
      if (!(result instanceof Error)) {
        dispatch(addMessage(new Message(t('VotingSuccess'), 'success')));
      }
      await actions.load();
    }
  }, [handleCheckAuth, dispatch, t, isAnswer, entity, _id, actions]);

  const handleAnswer = React.useCallback(() => {
    handleGiveAnswer(_id);
  }, [handleGiveAnswer, _id]);

  const handleToReword = React.useCallback(() => {
    handleCheckAuth();
  }, [handleCheckAuth]);

  const handleShare = React.useCallback(() => {
    setIsCopied(true);
    const link = `${window.location.origin}${window.location.pathname}`;
    clipboard.writeText(`${link}#${_id}`);
    setTimeout(() => {
      setIsCopied(false);
    }, 3000);
  }, [_id]);

  const handleReport = React.useCallback(() => {
    handleCheckAuth();
  }, [handleCheckAuth]);

  const handleSave = React.useCallback(async () => {
    if (handleCheckAuth()) {
      setLoading(true);
      const result = await dispatch(
        toggleFavorite(
          _id,
          isFavorite,
          isAnswer ? `professions/${entity}/answers` : `professions/${entity}`
        )
      );
      if (!(result instanceof Error)) {
        setIsFavorite(!isFavorite);
      }
      setLoading(false);
    }
  }, [handleCheckAuth, isFavorite, dispatch, entity, _id, isAnswer]);

  const isButtonHidden = React.useCallback(
    (index) => {
      // remove this condition after implementing the functionality
      if (TEMP_HIDDEN_BUTTONS.includes(index)) {
        return 'true';
      }

      return `${hiddenActions[index] && !fullActions}`;
    },
    [hiddenActions, fullActions]
  );

  React.useEffect(() => {
    if (!actionsRef.current) return;

    const parentWidth = actionsRef.current?.offsetWidth || 0;
    const children = [...(actionsRef.current?.children || [])];
    const childrenWidth =
      children.map((child) => child?.offsetWidth || 0).reduce((a, b) => a + b, 0) || 0;

    const showMore = parentWidth < childrenWidth;

    setShowMore(showMore);

    if (showMore) {
      setHiddenActions(() => {
        let position = 0;
        const isChildrenHidden = children.map((child) => {
          position += child.offsetWidth;
          return parentWidth < position;
        });

        return isChildrenHidden;
      });
    }
  }, [actionsRef]);

  if (readOnly) {
    return null;
  }

  if (type === 'advices') {
    return (
      <>
        <QuestionActions ref={actionsRef}>
          <VotesWrapper>
            <Tooltip title={t('VoteUp')}>
              <IconButtonStyled type={type} onClick={() => handleAddVote()}>
                <ArrowFatUpIcon />
              </IconButtonStyled>
            </Tooltip>
            <Votes type={type}>{votesTotal || 0}</Votes>

            <Tooltip title={t('VoteDown')}>
              <IconButtonStyled type={type} onClick={() => handleRemoveVote()}>
                <ArrowFatDownIcon />
              </IconButtonStyled>
            </Tooltip>
          </VotesWrapper>
          {isMobile ? (
            <Tooltip title={t('Share')}>
              <IconButtonStyled type={type} onClick={() => handleShare()}>
                <CopyIcon />
              </IconButtonStyled>
            </Tooltip>
          ) : (
            <ButtonStyled type={type} onClick={() => handleShare()} startIcon={<CopyIcon />}>
              {t('Share')}
            </ButtonStyled>
          )}

          {isMobile ? (
            <Tooltip title={t('Save')}>
              <IconButtonStyled onClick={() => handleSave()}>
                {isFavorite ? <StarCheckedIcon /> : <FavoritesIcon />}
              </IconButtonStyled>
            </Tooltip>
          ) : (
            <ButtonStyled
              onClick={() => handleSave()}
              type={type}
              isFavorite={`${isFavorite}`}
              stroke={theme.palette.white.main}
              startIcon={isFavorite ? <StarCheckedIcon /> : <FavoritesIcon />}>
              {t('Save')}
            </ButtonStyled>
          )}
        </QuestionActions>
        {loading ? <LinearProgress /> : null}
        {isCopied ? <SuccessMessage type={type}>{t('Copied')}</SuccessMessage> : null}
      </>
    );
  }

  return (
    <>
      <QuestionActions ref={actionsRef}>
        <VotesWrapper>
          <Tooltip title={t('VoteUp')}>
            <IconButtonStyled onClick={() => handleAddVote()}>
              <ArrowFatUpIcon />
            </IconButtonStyled>
          </Tooltip>
          <Votes>{votesTotal || 0}</Votes>

          <Tooltip title={t('VoteDown')}>
            <IconButtonStyled onClick={() => handleRemoveVote()}>
              <ArrowFatDownIcon />
            </IconButtonStyled>
          </Tooltip>
        </VotesWrapper>

        {!isAnswer ? (
          <>
            {isMobile ? (
              <Tooltip title={t('Answer')}>
                <IconButtonStyled onClick={() => handleAnswer()}>
                  <ChatIcon />
                </IconButtonStyled>
              </Tooltip>
            ) : (
              <ButtonStyled onClick={() => handleAnswer()} startIcon={<ChatIcon />}>
                {answers ? `[${answers}]` : null} {t('Answer')}
              </ButtonStyled>
            )}
          </>
        ) : null}

        {isMobile ? (
          <Tooltip title={t('ToReword')}>
            <IconButtonStyled hide={isButtonHidden(2)} onClick={() => handleToReword()}>
              <GoldMedalIcon />
            </IconButtonStyled>
          </Tooltip>
        ) : (
          <ButtonStyled
            hide={isButtonHidden(2)}
            onClick={() => handleToReword()}
            startIcon={<GoldMedalIcon />}>
            {t('ToReword')}
          </ButtonStyled>
        )}

        {isMobile ? (
          <Tooltip title={t('Share')}>
            <IconButtonStyled onClick={() => handleShare()}>
              <CopyIcon />
            </IconButtonStyled>
          </Tooltip>
        ) : (
          <ButtonStyled onClick={() => handleShare()} startIcon={<CopyIcon />}>
            {t('Share')}
          </ButtonStyled>
        )}

        {isMobile ? (
          <Tooltip title={t('Save')}>
            <IconButtonStyled onClick={() => handleSave()}>
              {isFavorite ? <StarCheckedIcon /> : <FavoritesIcon />}
            </IconButtonStyled>
          </Tooltip>
        ) : (
          <ButtonStyled
            onClick={() => handleSave()}
            startIcon={isFavorite ? <StarCheckedIcon /> : <FavoritesIcon />}>
            {t('Save')}
          </ButtonStyled>
        )}

        {isMobile ? (
          <Tooltip title={t('Report')}>
            <IconButtonStyled hide={isButtonHidden(5)} onClick={() => handleReport()}>
              <SkullIcon />
            </IconButtonStyled>
          </Tooltip>
        ) : (
          <ButtonStyled
            hide={isButtonHidden(5)}
            onClick={() => handleReport()}
            startIcon={<SkullIcon />}>
            {t('Report')}
          </ButtonStyled>
        )}

        {showMore && !isMobile ? (
          <Tooltip title={t('MoreActions')}>
            <IconButtonStyled onClick={() => setFullActions(!fullActions)}>
              <PencilDotsIcon />
            </IconButtonStyled>
          </Tooltip>
        ) : null}

        {children}
      </QuestionActions>
      {loading ? <LinearProgress /> : null}
      {isCopied ? <SuccessMessage>{t('Copied')}</SuccessMessage> : null}
    </>
  );
};

export default ActionBlock;
